import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth, useUser, useClerk } from '@clerk/clerk-react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/sso-callback')({
  component: SSOCallback,
})

function SSOCallback() {
  const { isLoaded, isSignedIn, userId, sessionId } = useAuth()
  const { user, isLoaded: userLoaded } = useUser()
  const { signOut } = useClerk()
  const navigate = useNavigate()

  // Poll for session establishment - Clerk might need time to process OAuth server-side
  useEffect(() => {
    if (!isLoaded || !userLoaded) {
      return
    }

    // If already signed in, check email and redirect
    if (isSignedIn && userId && user) {
      const email =
        user.primaryEmailAddress?.emailAddress ||
        user.emailAddresses?.[0]?.emailAddress ||
        ''

      if (email && !email.endsWith('@yhangry.com')) {
        toast.error('Only @yhangry.com email addresses are allowed')
        signOut().then(() => {
          navigate({ to: '/sign-in', replace: true })
        })
        return
      }

      navigate({ to: '/', replace: true })
      return
    }

    // If not signed in, poll for session establishment
    // Clerk processes OAuth server-side and may need time
    let attempts = 0
    const maxAttempts = 10 // 10 seconds total
    const pollInterval = 1000 // 1 second

    const pollForSession = setInterval(() => {
      attempts++

      if (isSignedIn && userId) {
        clearInterval(pollForSession)
        // The effect will re-run and handle the redirect
        return
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollForSession)
        toast.error('Sign in failed. Please try again.')
        navigate({ to: '/sign-in', replace: true })
      }
    }, pollInterval)

    return () => clearInterval(pollForSession)
  }, [
    isLoaded,
    userLoaded,
    isSignedIn,
    userId,
    user,
    navigate,
    signOut,
    sessionId,
  ])

  return (
    <div className='flex h-svh items-center justify-center'>
      <Loader2 className='size-8 animate-spin' />
      <p className='text-muted-foreground ml-4 text-sm'>
        Completing sign in...
      </p>
    </div>
  )
}
